import { Badge, Box, Button, Group, Modal, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconArrowLeft, IconArrowRight, IconRobotFace } from "@tabler/icons-react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { PortalUsageGuideStep } from "@/shared/constants/portalUsageGuide.types";

type PortalUsageGuideModalProps = {
  opened: boolean;
  onClose: () => void;
  stepIndex: number;
  onStepChange: (index: number) => void;
  steps: readonly PortalUsageGuideStep[];
  stepIndexForPath: (pathname: string) => number;
};

export function PortalUsageGuideModal({
  opened,
  onClose,
  stepIndex,
  onStepChange,
  steps,
  stepIndexForPath,
}: PortalUsageGuideModalProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isViewingCurrentPageTips = steps[stepIndex]?.path === location.pathname;

  useEffect(() => {
    if (!opened) return;
    onStepChange(stepIndexForPath(location.pathname));
  }, [opened, location.pathname, onStepChange, stepIndexForPath]);

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const progressLabel = useMemo(() => `Step ${stepIndex + 1} of ${steps.length}`, [stepIndex, steps.length]);

  if (!step) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={700}
      title={
        <Group gap="xs">
          <ThemeIcon color="primary.6" radius="xl" variant="light">
            <IconRobotFace size={18} />
          </ThemeIcon>
          <Text fw={700}>How to use GTsetu</Text>
        </Group>
      }
    >
      <Paper p="md" radius="md" style={{ border: "1px solid #E5E7EB" }}>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Badge color="primary.6" variant="light">
              {progressLabel}
            </Badge>
            <Text size="xs" c="dimmed" ta="right" maw={280}>
              {isViewingCurrentPageTips
                ? "Tips for the page you have open"
                : "Browse all topics with Previous / Next"}
            </Text>
          </Group>

          <Title order={4}>{step.title}</Title>

          <Box>
            <Text fw={700} size="sm" mb={4}>
              What this page is
            </Text>
            <Text c="dimmed" size="sm">
              {step.what}
            </Text>
          </Box>

          <Box>
            <Text fw={700} size="sm" mb={4}>
              Why this page matters
            </Text>
            <Text c="dimmed" size="sm">
              {step.why}
            </Text>
          </Box>

          <Box>
            <Text fw={700} size="sm" mb={6}>
              How to use it
            </Text>
            {step.howToUse.map((item) => (
              <Text key={item} size="sm" mb={6}>
                - {item}
              </Text>
            ))}
          </Box>

          <Group justify="space-between" mt="xs">
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => onStepChange(Math.max(0, stepIndex - 1))}
              disabled={isFirst}
            >
              Previous
            </Button>

            <Group>
              {step.path && (
                <Button
                  variant="light"
                  onClick={() => {
                    onClose();
                    navigate(step.path as string);
                  }}
                >
                  Open this section
                </Button>
              )}
              <Button
                rightSection={!isLast ? <IconArrowRight size={16} /> : undefined}
                onClick={() => {
                  if (isLast) {
                    onClose();
                    return;
                  }
                  onStepChange(Math.min(steps.length - 1, stepIndex + 1));
                }}
              >
                {isLast ? "Finish" : "Next"}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Paper>
    </Modal>
  );
}
